/* tslint:disable */
// @ts-nocheck
import { GraphData } from '../../types';

/**
 * Creates an ordered array of simplified leaf objects (name and stage) for a specific product
 * @param data The graph data containing leafs and flows
 * @param productName The name of the product to filter by
 * @returns An array of simplified leaf objects (name and stage) ordered by flow
 */
export function createOrderedLeafPathForProduct(data: GraphData, productName: string) {
  // Filter leafs that belong to the specified product
  const productLeafs = data.leafs.filter(leaf => 
    leaf.Products.includes(productName)
  );
  
  // Get all leaf names for this product
  const productLeafNames = new Set(productLeafs.map(leaf => leaf.Leaf));
  
  // Create a map for flow connections
  const flowMap = {};
  data.flows.forEach(flow => {
    if (!flowMap[flow.SourceLeaf]) {
      flowMap[flow.SourceLeaf] = [];
    }
    flowMap[flow.SourceLeaf].push(flow.TargetLeaf);
  });
  
  // Create a reverse flow map to find starting points
  const reverseFlowMap = {};
  data.flows.forEach(flow => {
    if (!reverseFlowMap[flow.TargetLeaf]) {
      reverseFlowMap[flow.TargetLeaf] = [];
    }
    reverseFlowMap[flow.TargetLeaf].push(flow.SourceLeaf);
  });
  
  // Find starting points (nodes that have no incoming flows or only incoming flows from outside the product)
  const startingPoints = Array.from(productLeafNames).filter(leaf => 
    !reverseFlowMap[leaf] || 
    !reverseFlowMap[leaf].some(source => productLeafNames.has(source))
  );
  
  // If no clear starting points, take any leaf from the product
  const initialStartPoints = startingPoints.length > 0 ? 
    startingPoints : 
    (productLeafs.length > 0 ? [productLeafs[0].Leaf] : []);
  
  // If no leafs for this product, return empty array
  if (initialStartPoints.length === 0) return [];
  
  // Create ordered path of leaf names
  const orderedLeafNames = [];
  const visited = new Set();
  
  // Process all starting points
  initialStartPoints.forEach(startPoint => {
    if (visited.has(startPoint)) return;
    
    // Add the starting point
    orderedLeafNames.push(startPoint);
    visited.add(startPoint);
    
    // Follow the flow from this starting point
    let current = startPoint;
    while (flowMap[current]) {
      // Find the next node that belongs to this product
      const nextNodes = flowMap[current].filter(next => 
        productLeafNames.has(next) && !visited.has(next)
      );
      
      if (nextNodes.length === 0) break;
      
      // Add the next node
      current = nextNodes[0];
      orderedLeafNames.push(current);
      visited.add(current);
    }
  });
  
  // Add any remaining nodes that weren't connected in the main flow
  Array.from(productLeafNames).forEach(leaf => {
    if (!visited.has(leaf)) {
      orderedLeafNames.push(leaf);
    }
  });
  
  // Create a map of leaf names to their stages
  // For duplicate leaf names, we'll use the first one's stage
  const leafStageMap = {};
  productLeafs.forEach(leaf => {
    if (!leafStageMap[leaf.Leaf]) {
      leafStageMap[leaf.Leaf] = leaf.Stage;
    }
  });
  
  // Create the final ordered array of simplified leaf objects
  const result = orderedLeafNames.map(leafName => ({
    name: leafName,
    stage: leafStageMap[leafName]
  }));
  
  return result;
}
